import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Cruise, Events, Flights, Hotel, Rental } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import SearchableSelect from '../../../componants/searchableSelect/SearchableSelect'
import { isRoundTripTrip } from '../../../utils/flightItinerary'
import { isEventService, normalizeServiceCode } from '../../../utils/serviceCode'
import './mytrips.css'

// Fallback only — real tabs/services (with live counts) come from the
// /users/trips response itself (`data.tabs`, `data.services`) and are used
// once loaded. This just avoids an empty toolbar on the very first render.
const FALLBACK_TABS = [
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
]

const FALLBACK_SERVICES = [
    { key: 'all', label: 'All' },
    { key: 'flight', label: 'Flights' },
    { key: 'hotel', label: 'Hotels' },
    { key: 'car-rental', label: 'Cars' },
]

const isCancelled = (booking) => {
    const status = String(booking.status || '').toLowerCase()
    return (
        booking.manage_tab === 'cancelled'
        || status.includes('cancel')
        || status === 'refunded'
        || status === 'failed'
    )
}

const serviceIcon = (trip) => {
    const code = String(trip.service || '').toLowerCase()
    if (code === 'hotel') return Hotel
    if (code === 'car-rental') return Rental
    if (code === 'cruise') return Cruise
    if (code === 'venue') return Hotel
    if (isEventService(code)) return Events
    return Flights
}

// Car-rental list items don't come with title/date_label/location like
// flight/hotel do — they carry car_name, provider_booking_id and a
// pickup/dropoff object with date_time/location_name instead.
const formatTripDate = (value) => {
    if (!value) return ''
    const parsed = new Date(value.replace(' ', 'T'))
    if (Number.isNaN(parsed.getTime())) return value
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const mapTrip = (trip) => {
    const serviceCode = String(trip.service || '').toLowerCase()
    const isCarRental = serviceCode === 'car-rental'
    return {
        id: trip.id,
        // Only meaningful for hotel/venue/event — the rating API's object_id
        // needs this underlying service record id, not the trip/booking id.
        serviceId: trip.service_id,
        service: serviceCode || 'flight',
        serviceProvider: trip.service_provider || '',
        detailPath: trip.detail_path || `/users/trips/${trip.id}`,
        status: trip.status_label || trip.status || '—',
        cancelled: isCancelled(trip),
        icon: serviceIcon(trip),
        title: (isCarRental ? trip.car_name : trip.title) || '—',
        code: (isCarRental ? trip.provider_booking_id : trip.subtitle) || '—',
        date: (isCarRental ? formatTripDate(trip.pickup?.date_time) : trip.date_label) || '—',
        location: (isCarRental ? trip.pickup?.location_name : trip.location) || '—',
        passengers: trip.passengers || '—',
        booked_rooms: trip.booked_rooms || '0',
        isRoundTrip: isRoundTripTrip(trip),

    }
}

const ArrowRight = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M3 9h11M10 5l4 4-4 4" stroke="#026D4D" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const TripCard = ({ trip, onOpen, onRate, canRate }) => (
    <div
        className="myTripCard"
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen() }}
    >
        <div className="myTripCard__top">
            <span className="myTripCard__id">ID: {trip.id}</span>
            <span className="myTripCard__topTags">
                {trip.isRoundTrip ? (
                    <span className="myTripCard__rtChip">Round-trip</span>
                ) : null}
                <span className={`myTripCard__status ${trip.cancelled ? 'myTripCard__status--cancelled' : ''}`}>
                    {trip.status}
                </span>
                {canRate && (
                    <button
                        type="button"
                        className="myTripCard__rateBtn"
                        onClick={(e) => { e.stopPropagation(); onRate(trip) }}
                    >
                        Rate
                    </button>
                )}
            </span>
        </div>
        <div className="myTripCard__main">
            <div className="myTripCard__service">
                <span className="myTripCard__icon">
                    <img src={trip.icon} alt="" />
                </span>
                <div className="myTripCard__info">
                    <div className="myTripCard__title">{trip.title}</div>

                    {trip.service !== 'hotel' && trip.code && trip.code !== '—' && (
                        <div className="myTripCard__code">{trip.code}</div>
                    )}
                </div>
            </div>
            <div className="myTripCard__meta myTripCard__meta--date">
                <span className="myTripCard__metaLabel">{trip.isRoundTrip ? 'Travel dates:' : 'Date:'}</span>
                <span className="myTripCard__metaValue">{trip.date}</span>
            </div>
            <div className="myTripCard__meta myTripCard__meta--location">
                <span className="myTripCard__metaLabel">{trip.isRoundTrip ? 'Round trip:' : 'Location:'}</span>
                <span className="myTripCard__metaValue" title={trip.location}>{trip.location}</span>
            </div>
            {/* <div className="myTripCard__meta myTripCard__meta--passengers">
                <span className="myTripCard__metaLabel">Passengers:</span>
                <span className="myTripCard__metaValue">{trip.passengers}</span>
            </div> */}
            {trip.service === 'travel_package' && trip.passengers && trip.passengers !== '—' && (
                <div className="myTripCard__meta myTripCard__meta--passengers">
                    <span className="myTripCard__metaLabel">Travelers:</span>
                    <span className="myTripCard__metaValue">{trip.passengers}</span>
                </div>
            )}
            {trip.service === 'hotel' && (
                <div className="myTripCard__meta myTripCard__meta--rooms">
                    <span className="myTripCard__metaLabel">Rooms</span>
                    <span className="myTripCard__metaValue">
                        {trip.booked_rooms}
                    </span>
                </div>
            )}
            <span className="myTripCard__arrow" aria-hidden="true">
                <ArrowRight />
            </span>
        </div>
    </div>
)

const Mytrips = () => {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState('upcoming')
    const [activeService, setActiveService] = useState('all')
    const [trips, setTrips] = useState([])
    const [loading, setLoading] = useState(true)
    const [tabsMeta, setTabsMeta] = useState(FALLBACK_TABS)
    const [servicesMeta, setServicesMeta] = useState(FALLBACK_SERVICES)

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        service
            .get(
                endpoints.users.trips,
                {
                    tab: activeTab,
                    service: activeService || 'all',
                    limit: 100,
                },
                { silent: true },
            )
            .then(({ data }) => {
                if (cancelled) return
                // Events ("My Events") and venues ("My RFPs") each have their own
                // dedicated page — keep them out of this generic list entirely.
                const excluded = new Set(['event', 'events', 'venue'])
                setTrips((data.data?.trips || []).filter((trip) => !excluded.has(normalizeServiceCode(trip.service))).map(mapTrip))
                if (data.data?.tabs?.length) setTabsMeta(data.data.tabs)
                if (data.data?.services?.length) setServicesMeta(data.data.services.filter((item) => !excluded.has(normalizeServiceCode(item.key))))
            })
            .catch(() => {
                if (!cancelled) setTrips([])
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => { cancelled = true }
    }, [activeTab, activeService])

    const activeTabLabel = tabsMeta.find((tab) => tab.key === activeTab)?.label || 'Trips'
    const sectionTitle = `${activeTabLabel} Trips`

    const handleOpenTrip = (trip) => {
        navigate(`/trip-doc?id=${trip.id}&service=${encodeURIComponent(trip.service || '')}`)
    }

    // Ratings can only be left against a specific completed booking — the
    // review form's "What are you rating?" dropdown uses car/event (see
    // reviewRating.jsx's SERVICE_OPTIONS) while trips here use car-rental/event.
    const handleRateTrip = (trip) => {
        const serviceType = trip.service === 'car-rental' ? 'car' : isEventService(trip.service) ? 'event' : trip.service
        // Rating API's object_id is the underlying hotel/venue/event record id
        // (/users/trips' `service_id`), not the booking's own `id` — confirmed
        // live 2026-09-24 (POST rejected service_rate=0 was a separate bug,
        // but the object_id-vs-service_id mismatch was flagged in the same pass).
        const objectId = serviceType === 'hotel' && trip.serviceId ? trip.serviceId : trip.id
        navigate('/profile', {
            state: {
                section: 'rating-reviews',
                prefill: {
                    serviceType,
                    objectId: String(objectId),
                    tripName: trip.title !== '—' ? trip.title : '',
                    // /users/trips returns the real vendor (e.g. "trawex") as
                    // service_provider — "self" is the documented fallback
                    // for bookings that don't come from a third-party vendor.
                    provider: trip.serviceProvider || 'self',
                },
            },
        })
    }

    return (
        <div className="myTrips">
            <div className="myTripsToolbar">
                <div className="commonBookingTab myTripsTabs">
                    {tabsMeta.map((tab) => (
                        <div
                            key={tab.key}
                            className={`bookTab ${activeTab === tab.key ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {/* Server counts include events/venue trips, which this page filters
                                out — only the active tab's count is trustworthy (it's the actual
                                length of what's on screen), so that's the only one shown. */}
                            {tab.label}{tab.key === activeTab && !loading ? ` (${trips.length})` : ''}
                        </div>
                    ))}
                </div>
                <div className="myTripsServiceFilter">
                    <SearchableSelect
                        options={servicesMeta.map((item) => ({ value: item.key, label: item.label }))}
                        value={activeService}
                        onChange={setActiveService}
                        ariaLabel="Filter by service"
                    />
                </div>
            </div>

            <div className="bsSectionTitle mt-4">{sectionTitle}</div>

            {loading && <p className="fare-extra-hint">Loading your trips...</p>}
            {!loading && !trips.length && (
                <p className="fare-extra-hint">
                    {activeService === 'all'
                        ? 'No trips here yet.'
                        : `No ${activeService} trips in this tab yet.`}
                </p>
            )}

            {!loading && trips.length > 0 && (
                <div className="myTripsList">
                    {trips.map((trip) => (
                        <TripCard
                            trip={trip}
                            key={`${trip.service}-${trip.id}`}
                            onOpen={() => handleOpenTrip(trip)}
                            onRate={handleRateTrip}
                            canRate={activeTab === 'completed' && !trip.cancelled}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

export default Mytrips
